import { useEffect, useState } from 'react';

import { Box, Button, Heading, Modal, Toast, useToast } from '@zillow/constellation-10';

import { RentalManagerUserClaimType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserClaimWebApiHandler_delete,
  useRentalManagerUserClaimWebApiHandler_list,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { combineContactMethods } from '../../../utils/listingClaimUtils';
import numberUtils from '../../../utils/numberUtils';

import { MANAGE_CLAIM_METHODS_STEPS } from './constants';

import DeleteClaimContact from './components/DeleteClaimContact';
import RemovableContactsList from './components/RemovableContactsList';

import ModalFooter from '../ModalFooter';

const { formatPhoneNumberInput } = numberUtils;
const { REMOVE_CLAIM_METHOD, VERIFY_CLAIM_CONTACT_TO_DELETE } = MANAGE_CLAIM_METHODS_STEPS;

type ManageClaimContactMethodsModalProps = {
  onClose: (contactDeleted?: boolean) => void;
};

const ManageClaimContactMethodsModal = ({ onClose }: ManageClaimContactMethodsModalProps) => {
  const [currentStep, setCurrentStep] = useState(REMOVE_CLAIM_METHOD);
  const [contactToDelete, setContactToDelete] = useState<{ value: string; type: string }>({ value: '', type: '' });
  const [contactDeleted, setContactDeleted] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const { enqueueToast } = useToast();

  const { data: listData, refetch: refetchList } = useRentalManagerUserClaimWebApiHandler_list();
  const {
    mutateAsync: deleteClaimMethod,
    isPending: isSubmitting,
    isError: hasError,
  } = useRentalManagerUserClaimWebApiHandler_delete();

  const contactMethods = listData ?? { emails: [], phones: [] };
  const contactMethodsCount = contactMethods.emails.length + contactMethods.phones.length;

  useEffect(() => {
    if (contactMethodsCount === 0) {
      setIsOpen(false);
      onClose();
    }
  }, [contactMethodsCount, onClose]);

  const incrementStep = () => {
    setCurrentStep((prev: string) =>
      prev === VERIFY_CLAIM_CONTACT_TO_DELETE ? REMOVE_CLAIM_METHOD : VERIFY_CLAIM_CONTACT_TO_DELETE,
    );
  };

  const handleRemoveContactMethod = () => {
    const { value, type } = contactToDelete;
    const isEmail = type === 'email';
    const formattedDeletedContact = isEmail ? value : formatPhoneNumberInput(value);
    const claimType = isEmail ? RentalManagerUserClaimType.EMAIL : RentalManagerUserClaimType.PHONE;

    deleteClaimMethod({ getParams: { type: claimType, value } })
      .then(() => {
        setContactDeleted(true);
        incrementStep();
        enqueueToast(<Toast tone="success">{`${formattedDeletedContact} removed`}</Toast>);
        return refetchList();
      })
      .catch(() => {
        setContactDeleted(true);
        incrementStep();
        enqueueToast(<Toast tone="critical">{`${formattedDeletedContact} couldn’t be removed. Try again.`}</Toast>);
      });
  };

  const headingText =
    currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE ? 'Confirm contact removal' : 'Manage contact methods';

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose(contactDeleted)}
      size={{ base: 'fullScreen', md: 'md' }}
      header={<Heading level={2}>{headingText}</Heading>}
      body={
        <Box css={{ marginBottom: '800' }} data-testid="ManageClaimContactsMethodsModal">
          {currentStep === REMOVE_CLAIM_METHOD && (
            <RemovableContactsList
              contactMethods={combineContactMethods(contactMethods)}
              incrementStep={incrementStep}
              onContactMethodSelection={setContactToDelete}
            />
          )}
          {currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE && contactToDelete.value && (
            <DeleteClaimContact contactToDelete={contactToDelete} />
          )}
        </Box>
      }
      footer={
        currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE ? (
          <ModalFooter
            hasError={hasError}
            isSubmitting={isSubmitting}
            cancelText="Back to manage contacts"
            submitText="Remove"
            onCancel={incrementStep}
            onSubmit={handleRemoveContactMethod}
          />
        ) : (
          <Button emphasis="filled" onClick={() => onClose(contactDeleted)}>
            Done
          </Button>
        )
      }
    />
  );
};

export default ManageClaimContactMethodsModal;
